/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Threshold Node
   Binary high-contrast black/white segmentation with smooth falloff.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_COLOR_UTILS } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const THRESHOLD_SHADER = `${GLSL_HEADER}
${GLSL_COLOR_UTILS}
uniform sampler2D u_image;
uniform float u_threshold;
uniform float u_smoothness;
uniform bool u_invert;

void main() {
  vec4 src = texture(u_image, v_uv);
  float lum = getLuminance(src.rgb);
  
  float edge0 = max(0.0, u_threshold - u_smoothness);
  float edge1 = min(1.0, u_threshold + u_smoothness);
  
  float mask = smoothstep(edge0, edge1, lum);
  if (u_invert) mask = 1.0 - mask;
  
  fragColor = vec4(vec3(mask), src.a);
}
`;

export const thresholdNode: NodeDefinition = {
  type: 'color.threshold',
  category: 'Color',
  label: 'Threshold',
  description: 'Converts image into high-contrast monochrome binary shapes',
  keywords: ['threshold', 'binary', 'black and white', 'monochrome', 'contrast', 'silhouette'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Output', type: 'image' },
  ],
  params: [
    {
      id: 'threshold',
      name: 'Threshold',
      type: 'slider',
      defaultValue: 50,
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
    {
      id: 'smoothness',
      name: 'Edge Softness',
      type: 'slider',
      defaultValue: 2,
      min: 0,
      max: 50,
      step: 0.5,
      unit: '%',
    },
    {
      id: 'invert',
      name: 'Invert',
      type: 'boolean',
      defaultValue: false,
      uiHint: 'toggle',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: THRESHOLD_SHADER,
    getUniforms: (inputs, params) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_threshold: (Number(params.threshold) || 50) / 100,
      u_smoothness: Math.max(0.001, (Number(params.smoothness) || 2) / 100),
      u_invert: Boolean(params.invert),
    }),
  }),
};
